/**
 * What the strategy actually traded, one cycle at a time.
 *
 * The Overview answers how it is going, in aggregates over periods. This
 * answers what it did — and until now nothing did: the finished cycles sat in
 * the ledger and had never been on a screen.
 *
 * The full-period Analytics snapshot owns totals. The paged ledger owns the
 * cycles that support them; it never adds the current page and presents that
 * partial number as the period result.
 *
 * Sorting and paging are the server's, not this page's. Reordering the fifty
 * rows already fetched would answer "the largest profit on this page" while
 * looking like it answered "the largest profit" — the classic way a sortable
 * table lies to the person reading it.
 */
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/design-system/Button";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { InstrumentSymbol } from "@/design-system/InstrumentSymbol";
import { Panel } from "@/design-system/Panel";
import { Empty, ErrorState, Forbidden, Loading } from "@/design-system/states/States";
import { exitText } from "@/shared/text/reasons";
import { useAccountAnalytics } from "@/shared/analytics/useAccountAnalytics";
import { projectAccountAnalytics } from "@/shared/analytics/presentation";
import type {
  CycleResponse,
  HistoryResponse,
  StrategyResultResponse,
} from "@/shared/api";
import {
  isNegative,
  isZero,
  money,
  signedMoney,
  signedPercent,
  toPlaces,
} from "@/shared/money/money";
import { ABSENT } from "@/shared/metric/metric";
import { versionLabel } from "@/shared/strategy/versions";
import { PeriodControl } from "@/shared/period/PeriodControl";
import { readPeriod, validatePeriod, writePeriod, type SelectedPeriod } from "@/shared/period/period";
import { humanAge } from "@/shared/time/age";

import { AnalyticsSummary } from "./AnalyticsSummary";
import { CoinPerformance } from "./CoinPerformance";
import { PnlChart } from "./PnlChart";
import { PoolPerformance } from "@/shared/pools/PoolPerformance";
import { NEWEST_FIRST, useHistory, type Sort } from "./useHistory";
import { CycleScoreExplanation } from "@/shared/strategy/CycleScoreExplanation";

/** One geometry for the head and every row, so a head sits over its column. */
const COLUMNS =
  "grid w-full grid-cols-[minmax(0,1fr)_6rem] items-center gap-x-3 gap-y-1 px-3 lg:px-4" +
  " @md:grid-cols-[minmax(0,1fr)_7rem_6rem_6rem]" +
  " @3xl:grid-cols-[minmax(0,1fr)_7rem_6rem_6rem_6rem_8rem]";

const CLOSED_CELL = "hidden @md:block @md:col-start-2";
const DEPLOYED_CELL = "hidden @3xl:block @3xl:col-start-3";
const NET_CELL = "col-start-2 @md:col-start-3 @3xl:col-start-4";
const RATE_CELL = "hidden @md:block @md:col-start-4 @3xl:col-start-5";
const HELD_CELL = "hidden @3xl:block @3xl:col-start-6";


/**
 * The colour a result carries.
 *
 * Read from the rounded figure and never from the sign of the string. `"0.00"`
 * does not start with a minus, so a break-even cycle was painted green — the
 * one reading a result colour must never give. The active table has had this
 * right all along; the two are the same function now.
 */
function tone(value: string): string {
  const shown = toPlaces(value);
  if (isZero(shown)) return "text-text-secondary";
  return isNegative(shown) ? "text-status-negative" : "text-status-positive";
}

function moment(instant: string): string {
  const at = new Date(instant);
  return `${String(at.getUTCDate()).padStart(2, "0")}.${String(
    at.getUTCMonth() + 1,
  ).padStart(2, "0")} ${String(at.getUTCHours()).padStart(2, "0")}:${String(
    at.getUTCMinutes(),
  ).padStart(2, "0")}`;
}

export function ClosedCycles(): React.JSX.Element {
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<Sort>(NEWEST_FIRST);
  // In the address, not in state: a filtered history is a place a person can
  // link to, come back to and share, and a filter kept only in memory is none
  // of those things.
  const [params, setParams] = useSearchParams();
  const symbol = params.get("symbol");
  // In the address for the same reason the symbol is: "what did BASE v2.0
  // actually trade last month" is a place a person links to and comes back to.
  const strategyVersion = params.get("strategy");
  const period = readPeriod(params);
  const validation = validatePeriod(period);
  const periodKey = `${period.preset}|${period.since ?? ""}|${period.until ?? ""}`;
  const previousPeriodKey = useRef(periodKey);
  const usable = validation.kind !== "invalid";
  const analytics = useAccountAnalytics(period);
  const resolved = analytics.data?.effective_since === null || analytics.data === undefined
    ? null
    : validatePeriod({
      since: analytics.data.effective_since,
      until: analytics.data.effective_until,
      preset: period.preset,
    });
  const matchesResolved =
    resolved?.kind === "exact" &&
    period.since === resolved.since &&
    period.until === resolved.until;
  const canonical =
    validation.kind === "exact"
      ? !(resolved?.kind === "exact" && !matchesResolved)
      : matchesResolved;
  const historyRange = !canonical
    ? null
    : validation.kind === "exact"
      ? validation
      : resolved?.kind === "exact"
        ? resolved
        : null;
  const state = useHistory(historyRange, offset, sort, symbol, strategyVersion);
  const analyticsHasNoRange = analytics.data?.effective_since === null;
  const historyIsTerminallyUnavailable =
    analyticsHasNoRange ||
    (historyRange === null &&
      (analytics.isError ||
        (analytics.data !== undefined && resolved?.kind === "invalid")));

  function setPeriod(next: SelectedPeriod, replace = false): void {
    setOffset(0);
    const nextParams = new URLSearchParams(params);
    for (const key of ["preset", "since", "until"]) nextParams.delete(key);
    writePeriod(next).forEach((value, key) => {
      nextParams.set(key, value);
    });
    setParams(nextParams, { replace });
  }

  useEffect(() => {
    if (previousPeriodKey.current !== periodKey) {
      previousPeriodKey.current = periodKey;
      setOffset(0);
    }
  }, [periodKey]);

  useEffect(() => {
    if (resolved?.kind === "exact" && !canonical) {
      setPeriod({ ...period, since: resolved.since, until: resolved.until }, true);
    }
  }, [canonical, period, resolved]);

  /**
   * A new order is a new record, not a rearranged page.
   *
   * Staying on page four while the ordering changes underneath would show a
   * slice of nothing in particular — rows forty to fifty of an arrangement the
   * reader never saw the start of.
   */
  function order(column: Sort["column"]): void {
    setOffset(0);
    setSort((was) =>
      was.column === column
        ? { column, order: was.order === "desc" ? "asc" : "desc" }
        : { column, order: "desc" },
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <PeriodControl
        period={period}
        effectiveSince={
          resolved?.kind === "exact"
            ? resolved.since
            : validation.kind === "exact"
              ? validation.since
              : null
        }
        effectiveUntil={
          resolved?.kind === "exact"
            ? resolved.until
            : validation.kind === "exact"
              ? validation.until
              : null
        }
        validationError={validation.kind === "invalid" ? validation.message : null}
        onPeriod={setPeriod}
      />
      {!usable ? (
        <Empty title="Укажите обе границы периода в UTC" detail="История появится после корректного интервала." />
      ) : (
        <>
          <AnalyticsEvidence analytics={analytics} resolved={resolved} />
          {historyIsTerminallyUnavailable ? (
            analyticsHasNoRange ? (
              <Empty
                title="Закрытых циклов нет"
                detail="Сервер подтвердил пустой полный период; точных границ для чтения истории нет."
              />
            ) : null
          ) : historyRange === null || !canonical || state.status === "loading" ? (
            <Loading title="Читаю историю…" />
          ) : state.status === "forbidden" ? (
            <Forbidden />
          ) : state.status === "unreadable" ? (
            <ErrorState
              title="Не могу прочитать историю"
              detail="Записи не пришли. Позиции и ордера на бирже от этого экрана не зависят."
            />
          ) : (
            <Record
              history={state.history}
              sort={sort}
              onSort={order}
              onPage={setOffset}
              symbol={symbol}
              strategyVersion={strategyVersion}
              onStrategy={(next: string | null) => {
                // A new lens is a new record, not a rearranged page: staying on
                // page four would show rows forty to fifty of a set the reader
                // never saw the start of.
                setOffset(0);
                const nextParams = new URLSearchParams(params);
                if (next === null) nextParams.delete("strategy");
                else nextParams.set("strategy", next);
                setParams(nextParams);
              }}
              onClear={() => {
                setOffset(0);
                const nextParams = new URLSearchParams(params);
                nextParams.delete("symbol");
                setParams(nextParams);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function AnalyticsEvidence({
  analytics,
  resolved,
}: {
  readonly analytics: ReturnType<typeof useAccountAnalytics>;
  readonly resolved: ReturnType<typeof validatePeriod> | null;
}): React.JSX.Element {
  if (analytics.isPending) return <Loading title="Читаю аналитику…" />;
  if (analytics.isError) {
    return (
      <ErrorState
        title="Не могу прочитать аналитику"
        detail="История циклов остаётся доступна отдельно. Повторите чтение аналитики позже."
      />
    );
  }
  const data = analytics.data;
  const evidence = projectAccountAnalytics(data);
  if (evidence === null) {
    return (
      <ErrorState
        title="Не могу прочитать аналитику"
        detail="Сервер вернул несогласованные границы и аналитические события."
      />
    );
  }
  if (data.effective_since !== null && resolved === null) {
    return (
      <Empty
        title="За этот период данных ещё нет"
        detail="Сервер не вернул точные границы аналитики."
      />
    );
  }
  if (data.effective_since !== null && resolved?.kind === "invalid") {
    return (
      <ErrorState
        title="Не могу прочитать аналитику"
        detail="Сервер вернул некорректные границы UTC."
      />
    );
  }
  const empty =
    data.summary.cycles === 0 &&
    data.portfolio.length === 0 &&
    data.realised.length === 0 &&
    data.pools.length === 0;

  return (
    <section aria-label="Аналитика за выбранный период" className="flex min-w-0 flex-col gap-3">
      {evidence.status === "stale" && (
        <p
          role="status"
          data-testid="analytics-stale"
          data-stale="true"
          className="flex flex-wrap gap-x-2 border-l-2 border-status-warning pl-3 text-body-main text-status-warning"
        >
          <span className="font-medium">Данные аналитики устарели.</span>
          <span className="text-text-secondary">
            Срез <time dateTime={evidence.asOf}>{evidence.asOf}</time> · {evidence.ageSeconds === null
              ? "возраст неизвестен"
              : `возраст ${humanAge(evidence.ageSeconds)}`}
          </span>
        </p>
      )}
      {empty && (
        <p
          className="border-l-2 border-border-strong pl-3 text-body-main text-text-secondary"
          data-testid="analytics-empty"
        >
          За выбранный период аналитических событий нет. Нулевой реализованный итог сохранён как ответ сервера.
        </p>
      )}
      <AnalyticsSummary analytics={data} evidence={evidence} />
      <div
        data-testid="analytics-primary-grid"
        className="grid min-w-0 grid-cols-1 items-start gap-3 xl:grid-cols-12"
      >
        {/* The chart takes eight of the twelve and the pools four, the same
            split the Overview uses. Both screens are reading the same two
            things against each other. */}
        <div className="min-w-0 xl:col-span-8">
          <PnlChart analytics={data} portfolioAvailable={evidence.portfolioAvailable} />
        </div>
        <PoolPerformance pools={data.pools} />
      </div>
      <CoinPerformance symbols={data.symbols ?? []} />
    </section>
  );
}

/**
 * What each strategy finished over the chosen period, and how fast.
 *
 * The account runs two strategies for as long as the migration takes, and this
 * record is the only place their claims can be checked one trade at a time. It
 * could not be: the rows named a pool and never a version, so "the new one is
 * faster" was a statement no screen could support or contradict.
 *
 * NET per capital-day leads, and the cards are ordered by it. A ranking by
 * daily takings is a ranking by how much capital each strategy was given —
 * over the last week the retiring strategy closed 158 cycles against 23 and
 * looked dominant for exactly that reason.
 *
 * Measured over the whole span, never over the page and never narrowed by the
 * lens: this is the reading a person uses to choose which lens to apply.
 */
function ByStrategy({
  results,
  selected,
  onSelect,
}: {
  readonly results: readonly StrategyResultResponse[];
  readonly selected: string | null;
  readonly onSelect: (version: string | null) => void;
}): React.JSX.Element | null {
  // One strategy is not a comparison. The block appears while the account runs
  // two and leaves the day it finishes running one.
  if (results.length < 2) return null;

  return (
    <section
      aria-labelledby="by-strategy-heading"
      data-testid="history-by-strategy"
      className="flex flex-col gap-2 rounded-panel border border-border-subtle bg-surface-default p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id="by-strategy-heading" className="text-label-caps text-text-secondary">
          Что закрыла каждая стратегия за период
        </h3>
        {selected !== null && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="by-strategy-clear"
            onClick={() => {
              onSelect(null);
            }}
            className="text-action-primary hover:text-action-hover pointer-coarse:min-h-11"
          >
            показать все
          </Button>
        )}
      </div>

      <div className="grid min-w-0 grid-cols-1 items-stretch gap-2 sm:grid-cols-2">
        {results.map((one) => {
          const active = one.strategy_version === selected;
          return (
            <Button
              key={one.strategy_version}
              variant="secondary"
              selected={active}
              aria-pressed={active}
              data-testid={`by-strategy-${one.strategy_version}`}
              title={`Показать только циклы ${versionLabel(one.strategy_version)}`}
              onClick={() => {
                onSelect(active ? null : one.strategy_version);
              }}
              className={`min-h-0 flex-col items-stretch gap-1 rounded-panel p-3 text-left ${
                active ? "ring-1 ring-action-primary" : ""
              }`}
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-label-caps text-text-primary">
                  {versionLabel(one.strategy_version)}
                </span>
                <span className="font-numeric text-helper-sm tabular-nums text-text-muted">
                  {`${String(one.closed_cycles)} закрыто`}
                </span>
              </span>
              <span className="flex items-end justify-between gap-3">
                <span className="flex flex-col gap-0.5">
                  <span className="text-helper-sm font-normal text-text-muted">
                    доход в день на капитал
                  </span>
                  <span
                    data-testid={`by-strategy-velocity-${one.strategy_version}`}
                    className={`font-numeric text-metric-md tabular-nums ${
                      one.net_per_capital_day === null
                        ? "text-text-secondary"
                        : one.net_per_capital_day.startsWith("-")
                          ? "text-status-negative"
                          : "text-status-positive"
                    }`}
                  >
                    {one.net_per_capital_day === null
                      ? ABSENT
                      : signedPercent(one.net_per_capital_day, 1)}
                  </span>
                </span>
                <span className="flex flex-col items-end gap-0.5 text-right">
                  <span className="text-helper-sm font-normal text-text-muted">NET</span>
                  <span
                    className={`font-numeric text-body-main tabular-nums ${
                      one.realised_net.startsWith("-")
                        ? "text-status-negative"
                        : "text-status-positive"
                    }`}
                    title={one.realised_net}
                  >
                    {signedMoney(one.realised_net)}
                  </span>
                </span>
              </span>
            </Button>
          );
        })}
      </div>
    </section>
  );
}

function Record({
  history,
  sort,
  onSort,
  onPage,
  symbol,
  strategyVersion,
  onStrategy,
  onClear,
}: {
  readonly history: HistoryResponse;
  readonly sort: Sort;
  readonly onSort: (column: Sort["column"]) => void;
  readonly onPage: (offset: number) => void;
  readonly symbol: string | null;
  readonly strategyVersion: string | null;
  readonly onStrategy: (version: string | null) => void;
  readonly onClear: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {history.by_strategy_truncated !== true && (
        // Absent rather than empty when the span outgrew one reading: a
        // breakdown that fell back to zeros would report two strategies that
        // stopped trading.
        <ByStrategy
          results={history.by_strategy ?? []}
          selected={strategyVersion}
          onSelect={onStrategy}
        />
      )}

      {symbol !== null && (
        // Named, and undoable in one click. A filtered record that looked like
        // the whole record would understate everything on the screen.
        <p
          className="flex flex-wrap items-center gap-2 text-body-main text-text-secondary"
          data-testid="history-filter"
        >
          <span>
            Только <span className="font-mono text-text-primary">{symbol}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            data-testid="history-filter-clear"
            className="text-action-primary hover:text-action-hover pointer-coarse:min-h-11"
          >
            показать все
          </Button>
        </p>
      )}

      <Panel
        title={
          strategyVersion === null
            ? `Закрытые циклы · ${String(history.total)}`
            : `Закрытые циклы · ${versionLabel(strategyVersion)} · ${String(
                history.total,
              )}`
        }
        testId="history-list"
        flush
      >
        <div className="@container">
          <Head sort={sort} onSort={onSort} />
          {history.cycles.length === 0 ? (
            <Empty
              title="За этот период ничего не закрылось"
              detail="Записи появятся, как только цикл завершится."
            />
          ) : (
            <ul>
              {history.cycles.map((one) => (
                <Row
                  key={one.cycle_id ?? `${one.symbol}-${one.closed_at}`}
                  cycle={one}
                />
              ))}
            </ul>
          )}
        </div>

        <Paging history={history} onPage={onPage} />
      </Panel>
    </div>
  );
}

function Head({
  sort,
  onSort,
}: {
  readonly sort: Sort;
  readonly onSort: (column: Sort["column"]) => void;
}): React.JSX.Element {
  return (
    <div
      data-testid="history-heads"
      className={`${COLUMNS} sticky top-0 z-10 border-b border-border-subtle bg-surface-default py-1.5`}
    >
      <Sortable column="symbol" sort={sort} onSort={onSort}>
        Инструмент
      </Sortable>
      <Sortable column="closed_at" sort={sort} onSort={onSort} className={CLOSED_CELL}>
        Закрыт
      </Sortable>
      <Sortable
        column="deployed"
        sort={sort}
        onSort={onSort}
        className={DEPLOYED_CELL}
        right
      >
        Вложено
      </Sortable>
      <Sortable column="net" sort={sort} onSort={onSort} className={NET_CELL} right>
        Результат
      </Sortable>
      <Sortable
        column="rate"
        sort={sort}
        onSort={onSort}
        className={RATE_CELL}
        right
        title="Результат к капиталу, вложенному в этот цикл. Два доллара на сотне и два на четырёхстах — разный результат."
      >
        К вложенному
      </Sortable>
      <Sortable column="held" sort={sort} onSort={onSort} className={HELD_CELL} right>
        В сделке
      </Sortable>
    </div>
  );
}

/**
 * One column head that orders the whole record.
 *
 * The arrow shows only on the column actually in force, and its direction
 * comes from the answer rather than from what was clicked: a head that marked
 * itself optimistically would keep pointing the wrong way after a request that
 * never arrived.
 */
function Sortable({
  column,
  sort,
  onSort,
  className = "",
  right = false,
  title,
  children,
}: {
  readonly column: Sort["column"];
  readonly sort: Sort;
  readonly onSort: (column: Sort["column"]) => void;
  readonly className?: string;
  readonly right?: boolean;
  readonly title?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const active = sort.column === column;
  const Arrow = sort.order === "desc" ? ArrowDown : ArrowUp;

  return (
    <Button
      variant="ghost"
      size="cell"
      data-testid={`sort-${column}`}
      data-active={active ? "true" : "false"}
      title={title}
      onClick={() => {
        onSort(column);
      }}
      className={[
        // The column classes show a row cell as a block; a head is a button
        // whose arrow sits beside its label, so it shows as flex instead.
        className.replaceAll(":block", ":flex"),
        "pointer-coarse:min-h-11 whitespace-nowrap",
        right ? "justify-end" : "justify-start",
        active ? "font-medium text-text-primary" : "",
      ].join(" ")}
    >
      {children}
      {active && <Arrow aria-hidden="true" className="size-3 shrink-0" />}
    </Button>
  );
}

/**
 * Which part of the record is on screen, and how to reach the rest.
 *
 * States the range and the total in figures rather than only offering arrows.
 * A reader who cannot tell whether they hold the first fifty of two hundred or
 * all of them has no reason to press anything.
 */
function Paging({
  history,
  onPage,
}: {
  readonly history: HistoryResponse;
  readonly onPage: (offset: number) => void;
}): React.JSX.Element | null {
  if (history.total === 0) return null;

  const first = history.offset + 1;
  const last = history.offset + history.cycles.length;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle px-3 py-2 lg:px-4"
      data-testid="history-paging"
    >
      <span className="text-helper-sm text-text-muted" data-testid="page-range">
        {`${String(first)}–${String(last)} из ${String(history.total)}`}
      </span>

      <span className="flex items-center gap-1">
        <Step
          label="Предыдущая страница"
          disabled={history.page <= 1}
          onClick={() => {
            onPage(Math.max(0, history.offset - history.limit));
          }}
          testId="page-back"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
        </Step>
        <span
          className="px-2 text-helper-sm tabular-nums text-text-secondary"
          data-testid="page-of"
        >
          {`${String(history.page)} / ${String(history.pages)}`}
        </span>
        <Step
          label="Следующая страница"
          disabled={history.page >= history.pages}
          onClick={() => {
            onPage(history.offset + history.limit);
          }}
          testId="page-forward"
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </Step>
      </span>
    </div>
  );
}

function Step({
  label,
  disabled,
  onClick,
  testId,
  children,
}: {
  readonly label: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly testId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function Row({ cycle }: { readonly cycle: CycleResponse }): React.JSX.Element {
  const [scoreOpen, setScoreOpen] = useState(false);
  const cycleId = cycle.cycle_id ?? null;
  return (
    <li
      className={`${COLUMNS} min-h-11 border-b border-border-subtle py-1.5 last:border-b-0 @md:min-h-9`}
      data-testid={`cycle-${cycle.symbol}`}
    >
      <span className="flex min-w-0 flex-col">
        <InstrumentSymbol symbol={cycle.symbol} />
        <span className="flex flex-wrap items-center gap-x-2 font-mono text-helper-sm text-text-muted">
          {/* Which strategy ran it, first on the line. The server has always
              sent this and the row dropped it, so a reader could see only the
              pool — PRIME, PULSE, CORE, FAST — and had to know the mapping to
              infer the version. During a migration whose whole question is
              which strategy performs better, the record where that is settled
              one trade at a time must state it rather than imply it. */}
          <span
            data-testid={`cycle-version-${cycle.symbol}`}
            className="text-text-secondary"
            title="Стратегия, по правилам которой шёл этот цикл"
          >
            {versionLabel(cycle.strategy_version)}
          </span>
          {/* Beside the instrument rather than in a column of its own: the pool
              is part of what the cycle was, and a new column would change the
              grid at every breakpoint for one short word. Two cycles with the
              same figures are not the same result if one waited for a five per
              cent move and the other for two. */}
          <span title="Пул, в котором шёл цикл">{cycle.pool}</span>
          <span title={cycle.exit_reason}>
            {exitText(cycle.exit_reason)}
          </span>
          {cycle.depth !== null && (
            <span title="Насколько глубоко цена уходила ниже входа">
              {signedPercent(cycle.depth)}
            </span>
          )}
          {cycleId !== null && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Обоснование Score"
              aria-expanded={scoreOpen}
              data-testid={`cycle-score-toggle-${cycleId}`}
              onClick={() => { setScoreOpen((current) => !current); }}
              className="min-h-0 px-1 text-action-primary hover:text-action-hover"
            >
              Обоснование Score
            </Button>
          )}
        </span>
      </span>

      <span
        className={`${CLOSED_CELL} font-mono text-helper-sm tabular-nums text-text-secondary`}
      >
        {moment(cycle.closed_at)}
      </span>

      <span
        className={`${DEPLOYED_CELL} text-right text-body-main tabular-nums text-text-secondary`}
        title={cycle.deployed}
      >
        {money(cycle.deployed)}
      </span>

      <span
        className={`${NET_CELL} text-right text-body-main tabular-nums ${tone(cycle.net)}`}
        title={cycle.net}
      >
        {signedMoney(cycle.net)}
      </span>

      <span
        className={`${RATE_CELL} text-right text-body-main tabular-nums ${tone(cycle.rate)}`}
      >
        {signedPercent(cycle.rate)}
      </span>

      <span
        className={`${HELD_CELL} text-right text-helper-sm tabular-nums text-text-muted`}
      >
        {humanAge(cycle.held_seconds)}
      </span>
      <CycleScoreExplanation cycleId={cycleId} enabled={scoreOpen} />
    </li>
  );
}
